import {
  useEffectWithController,
  useLoadableState,
  usePromise
} from '@carvana/merch-react-hooks';
import { useContext, useEffect, useState } from 'react';
import DeviceContext, { useIsMobile } from '~/context/common/DeviceContext';
import TradeContext from '~/context/common/TradeContext';
import {
  useFavoritesIds,
  useUserLocation,
  useIsLoggedIn
} from '~/context/common/UserContext';
import { RecommendedVehicle } from '~/contracts/vdp';
import { RequestedFeature } from '~/services/common/contracts';
import AppliedFilters from '~/services/common/contracts/AppliedFilters';
import { fetchRecommendations } from '~/services/vdp';
import { BrowserCookies } from '~/utilities';
import generateErrorLogger from '~/utilities/errorHandlers';

export default function useGetRecommendedVehicles(
  vehiclesToReturn: number,
  filters: Partial<AppliedFilters>,
  targetVehicleId: number | undefined
) {
  const { data, error, isLoaded, setData, setError, setLoading } =
    useLoadableState<RecommendedVehicle[]>([]);
  const [localVehicles, setLocalVehicles] = useState<RecommendedVehicle[]>([]);
  const mounted = usePromise();
  const isMobile = useIsMobile();
  const { browserName } = useContext(DeviceContext);
  const { isTradeInApplied } = useContext(TradeContext);
  const { zip = '' } = useUserLocation();
  const favoritesIds = useFavoritesIds();
  const isLoggedIn = useIsLoggedIn();

  useEffectWithController(
    controller => {
      if (!targetVehicleId) {
        setData([]);

        return;
      }

      const requestedFeaturesToUse = new Array<RequestedFeature>()
        .concat([
          'LoanTermPricing',
          'Personalization',
          'LocationBasedPrefiltering',
          'EarliestAcquisitionBoosting'
        ])
        .concat(
          isTradeInApplied && isLoggedIn
            ? ['ApplyTradeIn', 'TermsWithTradeInEquity']
            : []
        );

      async function fetchVehicles() {
        if (!targetVehicleId) return [];

        setLoading();

        return fetchRecommendations(
          {
            analyticsData: {
              searchRequestId: '',
              isMobileDevice: isMobile,
              browser: browserName,
              filterSource: 'notSpecified',
              referrer: ''
            },
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            filters: {
              ...filters
            },
            numVehiclesToReturn: vehiclesToReturn,
            requestedFeatures: requestedFeaturesToUse,
            vehicleId: targetVehicleId,
            zip5: zip
          },
          isLoggedIn,
          controller
        );

        // TODO current iteration of home redesign doesnt use delivery info. When we bring in the new tile we will want to go back to making this call.
        // const deliveryData = await getBulkAvailabilityInfo(
        //   vehicles,
        //   zip,
        //   '',
        //   userId
        // );
        //
        // return vehicles.map(vehicle => ({
        //   ...vehicle,
        //   ...deliveryData.find(
        //     vehicleData => vehicleData.vehicleId === vehicle.vehicleId
        //   )
        // }));
      }

      mounted(fetchVehicles())
        .then(vehicles => {
          setLocalVehicles(vehicles);
          if (vehicles.length === 0) setData([]);
        })
        .catch(
          generateErrorLogger(
            'Get recommended vehicles',
            {},
            { callback: setError }
          )
        );
    },
    [
      browserName,
      filters,
      isLoggedIn,
      isMobile,
      isTradeInApplied,
      mounted,
      setData,
      setError,
      setLoading,
      targetVehicleId,
      vehiclesToReturn,
      zip
    ]
  );

  useEffect(() => {
    if (localVehicles.length === 0) return;

    setData(
      localVehicles.map<RecommendedVehicle>(vehicle => ({
        ...vehicle,
        isFavorited: favoritesIds.includes(vehicle.vehicleId)
      }))
    );
  }, [favoritesIds, localVehicles, setData]);

  return {
    error,
    isLoaded,
    vehicles: data
  };
}
